import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Divider
} from '@mui/material';
import {
  StorefrontOutlined,
  SmartToy,
  Psychology,
  Search,
  Hub,
  SupportAgent,
  Add
} from '@mui/icons-material';
import { useAppContext } from '../../context/AppContext';

const channelTypeInfo = {
  ecommerce: {
    icon: <StorefrontOutlined />,
    color: '#1976d2',
    description: 'Create an online store, manage store styles, layouts, domains, and more'
  },
  chatgpt: {
    icon: <SmartToy />,
    color: '#10a37f',
    description: 'Enable customers to shop and checkout directly in ChatGPT'
  },
  gemini: {
    icon: <Psychology />,
    color: '#4285f4',
    description: 'Provide shopping experience in Google Gemini'
  },
  perplexity: {
    icon: <Search />,
    color: '#20808d',
    description: 'Integrate shopping functionality in Perplexity'
  },
  mcp: {
    icon: <Hub />,
    color: '#9c27b0',
    description: 'Universal MCP service, configurable in any AI Agent tool'
  },
  'shopping-agent': {
    icon: <SupportAgent />,
    color: '#f57c00',
    description: 'Embed conversational AI assistant in your website to help buyers find and purchase products'
  }
};

const SalesChannels = () => {
  const { salesChannels } = useAppContext();
  const navigate = useNavigate();

  const handleCreateChannel = (type) => {
    navigate(`/sales-channels/${type}`);
  };

  const newChannelTypes = [
    { type: 'ecommerce', name: 'E-commerce' },
    { type: 'chatgpt', name: 'OpenAI ChatGPT' },
    { type: 'gemini', name: 'Google Gemini' },
    { type: 'perplexity', name: 'Perplexity' },
    { type: 'mcp', name: 'Universal MCP' },
    { type: 'shopping-agent', name: 'Shopping Agent' }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Sales Channel Management</Typography>
      </Box>

      {/* Existing Channels */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Existing Channels
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {salesChannels.map((channel) => (
          <Grid item xs={12} sm={6} md={4} key={channel.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      backgroundColor: `${channelTypeInfo[channel.type]?.color}20`,
                      borderRadius: 1,
                      p: 1,
                      mr: 2,
                      display: 'flex'
                    }}
                  >
                    {React.cloneElement(channelTypeInfo[channel.type]?.icon || <Hub />, {
                      sx: { color: channelTypeInfo[channel.type]?.color }
                    })}
                  </Box>
                  <Box>
                    <Typography variant="h6">{channel.name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {channel.typeName}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Chip
                    label={channel.status === 'active' ? 'Active' : 'Inactive'}
                    color={channel.status === 'active' ? 'success' : 'default'}
                    size="small"
                  />
                  <Chip
                    label={channel.configStatus === 'complete' ? 'Configured' : 'In Progress'}
                    color={channel.configStatus === 'complete' ? 'primary' : 'warning'}
                    size="small"
                  />
                </Box>
                <Typography variant="body2" color="textSecondary">
                  Monthly GMV: €{channel.monthlyGMV.toLocaleString('en-US')}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Created: {channel.createdAt}
                </Typography>
              </CardContent>
              <CardActions>
                <Button size="small">View Details</Button>
                <Button size="small">Edit</Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Create New Channels */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Create New Channel
      </Typography>
      <Grid container spacing={3}>
        {newChannelTypes.map((channelType) => (
          <Grid item xs={12} sm={6} md={4} key={channelType.type}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      backgroundColor: `${channelTypeInfo[channelType.type]?.color}20`,
                      borderRadius: 1,
                      p: 1,
                      mr: 2,
                      display: 'flex'
                    }}
                  >
                    {React.cloneElement(channelTypeInfo[channelType.type]?.icon || <Hub />, {
                      sx: { color: channelTypeInfo[channelType.type]?.color, fontSize: 32 }
                    })}
                  </Box>
                  <Typography variant="h6">{channelType.name}</Typography>
                </Box>
                <Typography variant="body2" color="textSecondary">
                  {channelTypeInfo[channelType.type]?.description}
                </Typography>
              </CardContent>
              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button
                  startIcon={<Add />}
                  variant="contained"
                  onClick={() => handleCreateChannel(channelType.type)}
                  fullWidth
                  size="large"
                >
                  Create Channel
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default SalesChannels;
